import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import PublicPage from "@/components/public/PublicPage";
import { Button } from "@/components/ui/button";
import NotFound from "@/pages/NotFound";
import { BLOG_POSTS } from "@/data/blog";
import temperature from "@/assets/blog-temperature.png.asset.json";
import deviation from "@/assets/blog-deviation.png.asset.json";
import signature from "@/assets/blog-signature.png.asset.json";

function renderInline(text: string) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const [, label, href] = link;
      return href.startsWith("/")
        ? <Link key={i} to={href} className="text-accent-on-dark underline underline-offset-4">{label}</Link>
        : <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="text-accent-on-dark underline underline-offset-4">{label}</a>;
    }
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i} className="text-foreground">{bold[1]}</strong>;
    return part;
  });
}

const images = {
  temperature: { url: temperature.url, alt: "Crystal HACCP Quick Log showing temperature readings and flagged deviations" },
  deviation: { url: deviation.url, alt: "Crystal HACCP deviation record with a temperature observation and corrective actions" },
  signature: { url: signature.url, alt: "Crystal HACCP signature screen for signing a completed record" },
};

export function BlogIndex() {
  return (
    <PublicPage eyebrow="Field notes" title="Food-safety blog" intro="Practical guides for restaurant teams: daily records, corrective actions and keeping every location organised.">
      <section className="relative mx-auto max-w-6xl px-4 pb-20">
        <div className="grid gap-x-10 md:grid-cols-2">
          {BLOG_POSTS.map((post, i) => (
            <article key={post.slug} lang={post.lang} className="border-t border-border py-8">
              <p className="mb-3 text-sm text-accent-on-dark">0{i + 1} · {post.category}</p>
              <h2 className="mb-3 text-2xl text-foreground"><Link to={`/blog/${post.slug}`} className="hover:underline underline-offset-4">{post.title}</Link></h2>
              <p className="mb-5 text-muted-foreground">{post.description}</p>
              <Link to={`/blog/${post.slug}`} className="inline-flex items-center gap-2 text-accent-on-dark">{post.lang === "nb" ? "Les artikkelen" : "Read article"}<ArrowRight size={16} /></Link>
            </article>
          ))}
        </div>
        <div className="mt-8 border-t border-border pt-8">
          <Button asChild><Link to="/platform">Explore Crystal HACCP <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
        </div>
      </section>
    </PublicPage>
  );
}

export function BlogArticle() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((item) => item.slug === slug);
  if (!post) return <NotFound />;
  const image = images[post.image];
  const norwegian = post.lang === "nb";
  return (
    <div lang={post.lang}>
      <PublicPage eyebrow={post.category} title={post.title} intro={post.description}>
        <article className="relative mx-auto grid max-w-6xl gap-12 px-4 pb-20 lg:grid-cols-[minmax(0,1fr)_260px]">
          <div className="min-w-0">
            <div className="mb-8 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-border py-4 text-sm text-muted-foreground">
              <span>Crystal HACCP</span><time dateTime={post.published}>26 October 2026</time>
              <Link to="/blog" className="inline-flex items-center gap-2 text-accent-on-dark"><ArrowLeft size={14} />{norwegian ? "Alle artikler" : "All articles"}</Link>
            </div>
            <div className="space-y-10">
              {post.sections.map((section, i) => (
                <section key={section.heading} id={`section-${i}`} className="scroll-mt-24">
                  <h2 className="mb-4 text-2xl text-foreground">{section.heading}</h2>
                  <div className="space-y-4 leading-relaxed text-muted-foreground">
                    {section.paragraphs.map((paragraph) => <p key={paragraph}>{renderInline(paragraph)}</p>)}
                    {section.checklist && <ul className="list-disc space-y-2 pl-6">{section.checklist.map((item) => <li key={item}>{renderInline(item)}</li>)}</ul>}
                  </div>
                </section>
              ))}
            </div>
            <section className="mt-10 border-t border-border pt-8">
              <h2 className="mb-3 text-xl text-foreground">{norwegian ? "Kilder og videre lesning" : "Sources and further reading"}</h2>
              {post.sources.length > 0 && <ul className="space-y-3">{post.sources.map((source) => <li key={source.url}><a href={source.url} target="_blank" rel="noopener noreferrer" className="text-accent-on-dark underline underline-offset-4">{source.label}</a></li>)}</ul>}
              <p className="mt-5 text-sm text-muted-foreground">{norwegian ? "Generell veiledning, ikke juridisk rådgivning. Sjekk gjeldende veiledning og krav for egen virksomhet." : "General operational guidance, not legal advice. Check current authority guidance and requirements for your own business and location."}</p>
            </section>
            <section className="mt-10 border-t border-border pt-8">
              <h2 className="mb-4 text-2xl text-foreground">{norwegian ? "Samle kontrollene på mobil og web" : "Connect daily checks with your dashboard"}</h2>
              <div className="flex flex-wrap gap-3">
                <Button asChild><Link to="/platform">{norwegian ? "Se plattformen" : "Explore the platform"}<ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
                <Button asChild variant="outline"><Link to={post.productPath}>{post.productLabel}</Link></Button>
              </div>
            </section>
          </div>
          <aside className="space-y-8">
            <div>
              <p className="mb-4 text-sm font-semibold text-foreground">{norwegian ? "I denne artikkelen" : "In this article"}</p>
              <nav aria-label="Article contents" className="space-y-3">{post.sections.map((section, i) => <a key={section.heading} href={`#section-${i}`} className="block text-sm text-muted-foreground hover:text-foreground">{section.heading}</a>)}</nav>
            </div>
            <figure className="mx-auto max-w-[220px]">
              <img src={image.url} alt={image.alt} width={883} height={1920} loading="lazy" decoding="async" className="h-auto w-full rounded-lg border border-border" />
              <figcaption className="mt-3 text-sm text-muted-foreground">{norwegian ? "Fra Crystal HACCP-appen." : "From the Crystal HACCP app."}</figcaption>
            </figure>
          </aside>
        </article>
        <section className="relative border-t border-border">
          <div className="mx-auto max-w-6xl px-4 py-12">
            <h2 className="mb-6 text-2xl text-foreground">{norwegian ? "Relaterte artikler" : "Related reading"}</h2>
            <div className="grid gap-5 md:grid-cols-2">{BLOG_POSTS.filter((item) => item.slug !== post.slug).slice(0, 2).map((item) => <Link key={item.slug} to={`/blog/${item.slug}`} className="text-accent-on-dark underline underline-offset-4">{item.title}</Link>)}</div>
          </div>
        </section>
      </PublicPage>
    </div>
  );
}